import type { ICommandBarItemProps } from '@fluentui/react';
import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { TFunction } from 'i18next';
import { AIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { getSceneSelectionQuestion } from 'models/copilot/actions/aiEntityActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotAuditPreparationContent } from 'models/copilot/copilotAuditPreparation';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import {
  CopilotDocumentIngest,
  CopilotDocumentIngestSourceType,
  CopilotDocumentIngestStatus,
  CopilotDocumentStorageSourceType,
} from 'models/copilot/copilotDocumentIngest';
import { CopilotGenericContent } from 'models/copilot/copilotGeneric';
import {
  CopilotChatInputUsageProps,
  CopilotPanelContextDefinition,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
  CopilotPanelRuntimeView,
  CopilotWaitingFilesInput,
} from 'models/copilot/copilotPanelContextDefinition';
import { copilotPanelResultBus } from 'models/copilot/copilotPanelResultBus';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { CopilotProjectsContent } from 'models/copilot/copilotProjects';
import { CopilotScene, sceneEntityType } from 'models/copilot/copilotScene';
import { CopilotControlAuditContent } from 'models/copilot/Processes/ControlEvidenceAudit/copilotControlEvidenceAudit';
import { CopilotLibraryContent } from 'models/copilot/Processes/Library/ProcessLibraryContent';
import { CopilotProcessSuggestionsContent } from 'models/copilot/Processes/ProcessSuggestions/copilotProcessSuggestion';
import { TenantProcess } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import { apiCopilotGeneralKnowledgeCompletion, apiCopilotReferenceLibraryDocument } from 'services/Api/copilotService';
import { AutomationDraftingProcess } from './AutomationDrafting/automationDraftingProcess';
import { EntityEvidenceAuditProcess } from './EntityAudit/entityEvidenceAuditProcess';
import { getGenericSceneBindings } from './Generic/genericSceneBindings';
import { KpiReportProcess } from './KpiReport/kpiReportProcess';
import { SupplierProcesses } from './Supplier/supplierProcesses';
import { isStaleLibraryDocumentIngestResult } from './Panel/Documents/copilotDocumentIngestErrors';

export class CopilotGenericPanelContextDefinition extends CopilotPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.Generic;
    this.name = 'Generic';
    this.supportsChat = true;
  }

  // the scene's ready-made questions (Generic/genericSceneBindings.ts); a control or requirement shown in plain
  // chat (the control scene without its audit preview) also lists Draft automation
  getBindings(content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    return content instanceof CopilotGenericContent ? getGenericSceneBindings(content) : [];
  }

  getEntitySelectionQuestion(
    selectedEntityIds: number[],
    t: TFunction<string[]>,
    content?: CopilotPanelContentBase,
  ): string | undefined {
    if (!(content instanceof CopilotGenericContent) || content.scene === undefined) return undefined;
    if (selectedEntityIds.length === 0) return undefined;

    return getSceneSelectionQuestion(t, content.scene, selectedEntityIds.length);
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (content instanceof CopilotLibraryContent && content.selectedDocument) {
      return { entityType: EntityTypes.Link, entityId: content.selectedDocument.libraryLinkId };
    }

    // Plain chat is always answered through THIS definition (see useCopilotChatController), also for
    // usages that only add their own actions on top of it - so their entity has to be resolved here
    // or the backend would not know what the conversation is about.
    if (content instanceof CopilotAuditPreparationContent && content.internalAuditId) {
      return { entityType: EntityTypes.InternalAudit, entityId: content.internalAuditId };
    }

    if (content instanceof CopilotControlAuditContent && content.controlId) {
      return { entityType: EntityTypes.Control, entityId: content.controlId };
    }

    // the processes list under its own usage: the entity type without an id, as a Generic list scene sends it
    if (content instanceof CopilotProcessSuggestionsContent) {
      return { entityType: EntityTypes.Process, entityId: 0 };
    }

    if (content instanceof CopilotProjectsContent) {
      return content.deliverableTaskId
        ? { entityType: EntityTypes.Task, entityId: content.deliverableTaskId }
        : { entityType: EntityTypes.Project, entityId: content.projectId };
    }

    if (!(content instanceof CopilotGenericContent)) return undefined;

    // a list scene sends its entity type without an id (0): the API then loads that entity's tools and
    // reads a selection as rows of that type
    if (content.scene !== undefined && !content.entityId) {
      const sceneType = sceneEntityType(content.scene);

      return sceneType === undefined ? undefined : { entityType: sceneType, entityId: 0 };
    }

    if (content.entityType === undefined || !content.entityId) return undefined;

    return { entityType: content.entityType, entityId: content.entityId };
  }

  // The drafting process answers only while it RUNS (a start sets the action InProgress before this is called).
  // With one action, resolveRuntimeAction hands it over whatever its status, so a plain question on a scene
  // that merely offers the action must not start or finish the process: every other turn is plain chat.
  async getAnswer(runtime: CopilotPanelRuntime, t: TFunction<string[]>): Promise<void> {
    if (AutomationDraftingProcess.isRunning(runtime)) {
      await AutomationDraftingProcess.execute(runtime, this.getEntityRef(runtime.content));

      return;
    }

    // the two Supplier investigations (PLAN-073) answer every turn while they run
    if (SupplierProcesses.isRunning(runtime)) {
      await SupplierProcesses.execute(runtime, this.getEntityRef(runtime.content));

      return;
    }

    // the KPI report (PLAN-070) answers its start and its card; after the hand-back every turn is plain chat
    if (KpiReportProcess.isRunning(runtime)) {
      await KpiReportProcess.execute(runtime);

      return;
    }

    // the entity evidence audit (PLAN-077) answers its start only; the judge runs in the background
    if (EntityEvidenceAuditProcess.isRunning(runtime)) {
      await EntityEvidenceAuditProcess.execute(runtime, this.getEntityRef(runtime.content));

      return;
    }

    let entityRef = this.getEntityRef(runtime.content);
    let conversation = runtime.conversation;
    let sessionId = runtime.copilotSessionId;
    let selectedDocument = runtime.selectedDocument;
    const librarySelectedDocument =
      runtime.content instanceof CopilotLibraryContent ? runtime.content.selectedDocument : undefined;
    // While no document is previewed, the Library's selected row becomes the turn's document for a turn no
    // binding started (a typed, edited or answered question) and for a binding that asks for it (Summarize,
    // Create tasks from a report). The help question and the other prompts are about the page: they leave the
    // session's document as it is.
    const takesLibraryDocument = runtime.usesSelectedDocument ?? true;

    if (!selectedDocument && librarySelectedDocument && takesLibraryDocument) {
      // the question the user just typed lives only in the local conversation; the ingest hands back the
      // server's session conversation (which never saw the question) and replaces the local one below, so
      // carry the pending turn over or the completion is posted without a request and the question
      // vanishes from the panel
      const pendingRequest = runtime.conversation.request;
      const ingestRequest = new CopilotDocumentIngest();
      ingestRequest.contextKey = runtime.copilotContextKey;
      ingestRequest.sessionId = sessionId;
      ingestRequest.sourceType = CopilotDocumentIngestSourceType.Library;
      ingestRequest.storageSourceType = CopilotDocumentStorageSourceType.SharePoint;
      ingestRequest.libraryLinkId = librarySelectedDocument.libraryLinkId;
      ingestRequest.libraryListId = librarySelectedDocument.libraryListId;
      ingestRequest.title = librarySelectedDocument.title;
      ingestRequest.mimeType = librarySelectedDocument.mimeType;

      const ingestResult = await apiCopilotReferenceLibraryDocument(ingestRequest, runtime.accessToken);
      const content = ingestResult?.content;
      if (isStaleLibraryDocumentIngestResult(ingestResult)) {
        entityRef = undefined;
      } else {
        if (
          ingestResult?.status !== CopilotDocumentIngestStatus.Ready ||
          !content?.conversation ||
          content.sessionId === undefined ||
          !content.selectedDocument
        ) {
          throw new Error(ingestResult?.message || t('copilot:Chat.Document.IngestFailed'));
        }

        conversation = content.conversation;
        if (pendingRequest) {
          conversation.request = pendingRequest;
          conversation.thread.push(pendingRequest);
        }
        sessionId = content.sessionId;
        selectedDocument = content.selectedDocument;
        runtime.setCopilotSessionId(sessionId);
        runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, conversation));
      }
    }

    // the field only belongs to the request when it is a field of the entity the request is about
    const activeField =
      runtime.activeField &&
      entityRef &&
      runtime.activeField.entityType === entityRef.entityType &&
      runtime.activeField.entityId === entityRef.entityId
        ? runtime.activeField
        : undefined;

    // the page a list scene or the dashboard shows, by name: the help question explains that page
    const scene =
      runtime.content instanceof CopilotGenericContent && runtime.content.scene !== undefined
        ? CopilotScene[runtime.content.scene]
        : undefined;

    const answer = await apiCopilotGeneralKnowledgeCompletion(
      conversation,
      runtime.connectionId,
      runtime.accessToken,
      sessionId,
      runtime.copilotContextKey,
      entityRef,
      selectedDocument,
      runtime.instructionExternalId,
      runtime.abortSignal,
      activeField,
      runtime.skill,
      runtime.answeredQuestionSetId,
      scene,
      runtime.sessionTitle,
      KpiReportProcess.getActiveProcess(runtime),
    );
    if (answer) {
      runtime.setCopilotSessionId(answer.sessionId);
      runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));

      // the agent may have changed the entity the scene is showing (the audit agent plans days and
      // assigns work); the scene decides whether this concerns it
      copilotPanelResultBus.publishGeneralCompletion(
        runtime.content?.context ?? CopilotPanelContext.Generic,
        entityRef?.entityType,
        entityRef?.entityId,
      );
    }
  }

  //
  // The drafting process (phase 27), the KPI report process (PLAN-070), the Supplier investigations (PLAN-073) and the
  // entity evidence audit (PLAN-077)
  //

  canStartChat(actions: CopilotAction[], runtime?: CopilotPanelRuntimeView): boolean {
    if (runtime && AutomationDraftingProcess.isRunning(runtime)) return AutomationDraftingProcess.canStartChat(runtime);
    if (runtime && KpiReportProcess.isRunning(runtime)) return KpiReportProcess.canStartChat(runtime);
    if (runtime && SupplierProcesses.isRunning(runtime)) return SupplierProcesses.canStartChat(runtime);
    if (runtime && EntityEvidenceAuditProcess.isRunning(runtime))
      return EntityEvidenceAuditProcess.canStartChat(runtime);

    return super.canStartChat(actions, runtime);
  }

  getChatInputUsageProps(runtime: CopilotPanelRuntime): CopilotChatInputUsageProps {
    return {
      ...super.getChatInputUsageProps(runtime),
      placeholder:
        AutomationDraftingProcess.getChatInputPlaceholder(runtime) ??
        KpiReportProcess.getChatInputPlaceholder(runtime) ??
        SupplierProcesses.getChatInputPlaceholder(runtime) ??
        EntityEvidenceAuditProcess.getChatInputPlaceholder(runtime),
    };
  }

  getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    return (
      AutomationDraftingProcess.getWelcomeMessage(action, t) ??
      KpiReportProcess.getWelcomeMessage(action, t) ??
      SupplierProcesses.getWelcomeMessage(action, t) ??
      EntityEvidenceAuditProcess.getWelcomeMessage(action, t) ??
      super.getWelcomeMessage(action, t)
    );
  }

  getProgressRef(runtime: CopilotPanelRuntimeView): CopilotPanelProgressRef | undefined {
    return (
      AutomationDraftingProcess.getProgressRef(runtime, this.getEntityRef(runtime.content)) ??
      KpiReportProcess.getProgressRef(runtime) ??
      SupplierProcesses.getProgressRef(runtime, this.getEntityRef(runtime.content)) ??
      EntityEvidenceAuditProcess.getProgressRef(runtime, this.getEntityRef(runtime.content))
    );
  }

  isPersistedProcessStateReady(state: CopilotPanelProcessState): boolean {
    return EntityEvidenceAuditProcess.isPersistedProcessStateReady(state) ?? super.isPersistedProcessStateReady(state);
  }

  getPreviewCommands(runtime: CopilotPanelRuntime, t: TFunction<string[]>): ICommandBarItemProps[] {
    const supplierCommands = SupplierProcesses.getPreviewCommands(runtime, t);

    return supplierCommands.length > 0 ? supplierCommands : AutomationDraftingProcess.getPreviewCommands(runtime, t);
  }

  // the drafting process's cards; the field proposal card is answered by the shell (TextActions/fieldProposalCard.ts) first
  async onAdaptiveCardSubmit(
    runtime: CopilotPanelRuntime,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
    t: TFunction<string[]>,
  ): Promise<boolean> {
    return (
      AutomationDraftingProcess.onAdaptiveCardSubmit(runtime, block, payload, t) ||
      KpiReportProcess.onAdaptiveCardSubmit(runtime, block, payload) ||
      SupplierProcesses.onAdaptiveCardSubmit(runtime, block, payload, t) ||
      EntityEvidenceAuditProcess.onAdaptiveCardSubmit(runtime, payload)
    );
  }

  // the supplier compliance check takes the person's certificates at its request and upload cards (PLAN-CTI-092)
  acceptsFilesWhileWaiting(runtime: CopilotPanelRuntimeView): boolean {
    return SupplierProcesses.acceptsFilesWhileWaiting(runtime);
  }

  onFilesWhileWaiting(runtime: CopilotPanelRuntime, input: CopilotWaitingFilesInput, t: TFunction<string[]>): void {
    if (SupplierProcesses.acceptsFilesWhileWaiting(runtime)) SupplierProcesses.onFilesWhileWaiting(runtime, input, t);
  }

  applyProcessState(runtime: CopilotPanelRuntime, state: CopilotPanelProcessState): CopilotPreviewContent | undefined {
    return (
      AutomationDraftingProcess.applyProcessState(runtime, state) ??
      KpiReportProcess.applyProcessState(runtime, state) ??
      SupplierProcesses.applyProcessState(runtime, state) ??
      EntityEvidenceAuditProcess.applyProcessState(runtime, state) ??
      super.applyProcessState(runtime, state)
    );
  }

  getStopRef(runtime: CopilotPanelRuntimeView, action: CopilotAction): CopilotPanelEntityRef | undefined {
    return KpiReportProcess.getStopRef(runtime, action) ?? super.getStopRef(runtime, action);
  }

  // the KPI report a chat turn started on a scene that does not list it (PLAN-070), and a Supplier investigation an
  // assessment's card started there (PLAN-073)
  getFollowedProcessAction(processId: TenantProcess, t: TFunction<string[]>): CopilotAction | undefined {
    return (
      KpiReportProcess.getFollowedAction(processId, t) ??
      SupplierProcesses.getFollowedAction(processId, t) ??
      super.getFollowedProcessAction(processId, t)
    );
  }

  // the processes this usage runs from any scene, besides the ones its bindings list
  ownsProcess(processId: TenantProcess, content: CopilotPanelContentBase | undefined): boolean {
    return (
      KpiReportProcess.isReportAction({ processId } as CopilotAction) ||
      SupplierProcesses.isSupplierAction({ processId } as CopilotAction) ||
      super.ownsProcess(processId, content)
    );
  }

  clearPreview(runtime: CopilotPanelRuntime): void {
    AutomationDraftingProcess.clearPreview(runtime);
    KpiReportProcess.clearPreview(runtime);
    SupplierProcesses.clearPreview(runtime);
    EntityEvidenceAuditProcess.clearPreview(runtime);
  }
}
